import { gql } from '@apollo/client';

/**
 * Spec 0018 (career center), increment A — ERP-scope queries.
 *
 * Operation names are deliberately different from the backend field names
 * (`careerMembers`, `careerDirections`, `careerLessonsErp`) to avoid the
 * codegen result-type collapse.
 *
 * Wire-level enums are plain Strings: member `status` is
 * `active|paused|placed|archived`, lesson `completionKind` is
 * `acknowledge|artifact`. They are narrowed in the `core/hooks/CareerCenter`
 * model layer, never inline in components.
 */

export const CareerMembersListQuery = gql`
  query ErpCareerMembers($status: String, $directionId: ID, $search: String) {
    careerMembers(status: $status, directionId: $directionId, search: $search) {
      id
      studentId
      studentName
      studentEmail
      groupId
      groupName
      directionId
      directionTitle
      status
      enrolledAt
      notes
      lessonsCompleted
      lessonsTotal
    }
  }
`;

/**
 * Increment B1 — one member's full card, including the nested structured
 * `profile` the graduate builds in the LMS (nullable: a member may not have
 * started one). Operation name stays distinct from the `careerMemberDetail`
 * field so codegen keeps a dedicated result type. ERP reads this profile
 * read-only; editing lives in the LMS.
 */
export const CareerMemberDetailQuery = gql`
  query ErpCareerMemberDetail($id: ID!) {
    careerMemberDetail(id: $id) {
      id
      studentName
      studentEmail
      profile {
        id
        firstName
        lastName
        email
        phone
        photoUrl
        city
        country
        relocationReady
        workMode
        workPermit
        availableFrom
        seniority
        desiredPosition
        desiredSalary
        githubUrl
        linkedinUrl
        portfolioUrl
        customSkills
        isSearchable
        contactsVisible
        completeness
        skills {
          skillId
          name
          category
          level
          years
        }
        projects {
          id
          title
          description
          role
          repoUrl
          demoUrl
          technologyNames
        }
        experience {
          id
          company
          position
          description
          startYear
          startMonth
          endYear
          endMonth
          isCurrent
        }
        education {
          id
          institution
          degree
          specialization
          startYear
          startMonth
          endYear
          endMonth
        }
        languages {
          id
          language
          level
        }
        certificates {
          id
          title
          issuer
          issuedAt
          url
          fileUrl
        }
        locales {
          language
          jobTitle
          about
        }
      }
    }
  }
`;

export const CareerDirectionsQuery = gql`
  query ErpCareerDirections($includeInactive: Boolean) {
    careerDirections(includeInactive: $includeInactive) {
      id
      slug
      title
      order
      isActive
    }
  }
`;

/**
 * The staff-side lesson list. Unlike the LMS `careerLessons`, this returns
 * unpublished lessons too and never nulls `content` — ERP edits the track.
 *
 * Spec 0020 (authoring parity) adds `videoUrl` and `attachments`: the media the
 * ERP lesson form now uploads. The `attachments` selection is kept
 * byte-for-byte identical to the one in the video/attachment mutations
 * (`mutations/careerCenter.ts`) so the query- and mutation-derived attachment
 * types stay structurally interchangeable in the model layer.
 *
 * Spec 0020 Фаза B/C also adds the staff completion funnel (`stats`, nullable —
 * the backend serves it only in ERP scope) and the self-check quiz
 * (`quizQuestions`, whose `correctIndexes` IS populated for ERP authors — the
 * LMS scope masks it). The `quizQuestions` selection matches the one returned by
 * `SaveCareerLessonQuiz` field-for-field so the two types are interchangeable.
 */
export const CareerLessonsErpQuery = gql`
  query ErpCareerLessons {
    careerLessonsErp {
      id
      order
      title
      summary
      content
      durationMin
      completionKind
      artifactHint
      isPublished
      videoUrl
      attachments {
        id
        originalName
        contentType
        sizeBytes
        url
      }
      stats {
        openedCount
        completedCount
        membersTotal
      }
      quizQuestions {
        id
        order
        text
        options
        correctIndexes
      }
    }
  }
`;

/**
 * Spec 0020 Фаза B — one member's per-lesson learning map for the progress
 * drawer (`careerMemberProgress`, ERP scope + CAREER_ROLES). Op name stays
 * distinct from the field so codegen keeps a dedicated result type. Every field
 * is non-null except the two timestamps.
 */
export const CareerMemberProgressQuery = gql`
  query CareerMemberLearningProgress($memberId: ID!) {
    careerMemberProgress(memberId: $memberId) {
      lessonId
      order
      title
      isOpened
      openedAt
      openCount
      isCompleted
      completedAt
    }
  }
`;

/**
 * Increment B — the Вакансии tab (`careerVacanciesErp`). Unlike the LMS
 * `careerVacancies`, this returns every status (DRAFT included) so staff can
 * run the DRAFT→PUBLISHED workflow. Operation name stays distinct from the
 * `careerVacanciesErp` field to avoid the codegen result-type collapse.
 *
 * Wire-level enums are plain Strings (`status`, `workMode`, `seniority`,
 * `employmentType`) — narrowed in the `core/hooks/CareerCenter` model layer.
 */
export const VacanciesErpListQuery = gql`
  query ErpVacancies($status: String, $directionId: ID, $search: String) {
    careerVacanciesErp(
      status: $status
      directionId: $directionId
      search: $search
    ) {
      id
      title
      companyName
      applyUrl
      location
      workMode
      seniority
      employmentType
      salaryMin
      salaryMax
      salaryCurrency
      description
      status
      publishedAt
      expiresAt
      createdAt
      directionIds
      directionTitles
      skillIds
      skillNames
      appliedCount
    }
  }
`;

/** One vacancy by id (staff scope — any status). */
export const VacancyErpDetailQuery = gql`
  query ErpVacancy($id: ID!) {
    careerVacancyErp(id: $id) {
      id
      title
      companyName
      applyUrl
      location
      workMode
      seniority
      employmentType
      salaryMin
      salaryMax
      salaryCurrency
      description
      status
      publishedAt
      expiresAt
      createdAt
      directionIds
      directionTitles
      skillIds
      skillNames
      appliedCount
    }
  }
`;

/** Funnel counters for the Дашборд tab. */
export const CareerDashboardQuery = gql`
  query ErpCareerDashboard {
    careerDashboard {
      members
      trained
      profileReady
      applied
      placed
      vacanciesPublished
    }
  }
`;

/**
 * Skill reference for the vacancy-form multiselect. The backend field is
 * `careerSkills(category, search)`; the operation name is kept distinct.
 */
export const CareerSkillsQuery = gql`
  query ErpCareerSkills($category: String, $search: String) {
    careerSkills(category: $category, search: $search) {
      id
      name
      slug
      category
    }
  }
`;
